/**
 * Pack Builder frequency changes
 * — Works out where a subscriber's upcoming charge should land when they switch
 *   how often they get a pack from the edit-pack builder.
 *
 * Recharge leaves the queued charge on its old date when the interval changes, and
 * the builder pins that date on purpose (d4349b2) because Recharge's own
 * recalculation lands on days that aren't the customer's delivery-lane day. So a
 * weekly subscriber who picked "every 2 weeks" still got one more order a week after
 * their last, and CX was skipping that order by hand to make the change "take".
 * This moves that charge out instead, a whole week at a time so the weekday (and the
 * Nextime lane) stays the same.
 */
(function (root) {
  "use strict";

  const DAY_MS = 24 * 60 * 60 * 1000;

  // A first order placed on a Monday for a Thursday lane has its first recurring
  // charge 10 days later, a Wednesday checkout on a Tuesday lane 13. The 13-day one
  // already reads as "every 2 weeks"; the 10-day one does not. Three days of slack
  // separates the two without moving charges that are already far enough out.
  const GRACE_DAYS = 3;

  // Charges that turned into an order. A refunded order was still delivered, so it
  // still anchors the schedule.
  const PROCESSED_STATUSES = ["success", "refunded", "partially_refunded"];

  function dayValue(date) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date || ""));
    if (!match) return null;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  function isoDay(value) {
    return new Date(value).toISOString().slice(0, 10);
  }

  // Days in a Recharge interval ("week"/"weeks"/"day"). Months are not a whole number
  // of weeks, so they get null and are never moved.
  function intervalDays(frequency, unit) {
    const count = Number(frequency);
    if (!Number.isFinite(count) || count <= 0) return null;
    const normalized = String(unit || "")
      .toLowerCase()
      .replace(/s$/, "");
    if (normalized === "week") return count * 7;
    if (normalized === "day") return count;
    return null;
  }

  // Scheduled date (YYYY-MM-DD) of the most recent processed charge on or before
  // `onOrBefore`, or null when there is none.
  function lastProcessedChargeDate(charges, onOrBefore) {
    const limit = dayValue(onOrBefore);
    let latest = null;
    (charges || []).forEach((charge) => {
      if (!charge) return;
      const status = String(charge.status || "").toLowerCase();
      if (!PROCESSED_STATUSES.includes(status)) return;
      const scheduled = dayValue(charge.scheduled_at);
      if (scheduled === null || (limit !== null && scheduled > limit)) return;
      if (latest === null || scheduled > latest) latest = scheduled;
    });
    return latest === null ? null : isoDay(latest);
  }

  // The next charge date to send with the new interval. Returns `nextCharge` itself
  // unless the customer is moving to a longer interval and that charge comes sooner
  // than the new interval (less GRACE_DAYS) after their last order; then it moves out
  // by whole weeks.
  function nextChargeDate({ nextCharge, lastCharge, fromDays, toDays }) {
    const next = dayValue(nextCharge);
    const last = dayValue(lastCharge);
    if (next === null || last === null || !fromDays || !toDays || toDays <= fromDays) {
      return nextCharge;
    }
    const gap = Math.round((next - last) / DAY_MS);
    const needed = toDays - GRACE_DAYS;
    if (gap >= needed) return nextCharge;
    const weeks = Math.ceil((needed - gap) / 7);
    return isoDay(next + weeks * 7 * DAY_MS);
  }

  // Whole days from one YYYY-MM-DD date to another.
  function daysBetween(from, to) {
    const start = dayValue(from);
    const end = dayValue(to);
    if (start === null || end === null) return null;
    return Math.round((end - start) / DAY_MS);
  }

  root.PackBuilderFrequency = {
    GRACE_DAYS,
    intervalDays,
    lastProcessedChargeDate,
    nextChargeDate,
    daysBetween,
  };
})(typeof window !== "undefined" ? window : this);
